import { playerActions, selectPlayerState } from "app/store/slices/player";
import { useAppDispatch, useAppSelector } from "app/store/store";
import React, { useEffect, useMemo, useRef, useState } from "react";
import MemoPlay from "../icons/Play";
import styles from "./player.module.scss";
import { Category, RadioLiveMetadata, Stream } from "services-hooks/types";
import { useGetPillowRadioLive } from "services-hooks/hooks";
import MemoPause from "../icons/Pause";
import { useInterval } from "react-use";
import clsx from "clsx";
import { useSpacebarEvent } from "app/hooks/spacebar.hook";
import MemoLoader from "../icons/Loader";
import { useFavorites } from "app/hooks/favorites.hook";
import { useErrorToast, useToast } from "../toast/toast.component";
import { usePlayerContext } from "app/context/player.context";
import { PlayerFullScreen } from "./player-fullscreen.component";
import { getPillowRadioLive } from "services-hooks/services";
import MemoWave from "../icons/Wave";
import { AdStream, TargetspotEventType } from "app/utils/audioAds";
import useAppAudio, { HTMLMediaState } from "app/hooks/audio.hook";
import {
  getArtistName,
  getStationLiveLogo,
  getStationTitle,
} from "app/model/category";
import { useQueryClient } from "react-query";
import { selectUserState, userActions } from "app/store/slices/user";
import { useTranslate } from "app/hooks/lang";
import { useNavigatePath } from "app/hooks/navigation.hook";
import { useCallback } from "react";
import { useFeaturesContext } from "app/context/features.context";
import { SwaggerResponse } from "services-hooks/config";
import { useRouter } from "next/router";

interface PlayerStreamProps {
  category: Category;
  playerOpen: boolean;
  setPlayerOpen: (open: boolean) => void;
  streams: Stream[];
  streamsLoadError: boolean;
  refetchStreams: VoidFunction;
  adStreams: AdStream[];
  streamsLoading: boolean;
  sendTargetspotTimeUpdateEvent: (state: HTMLMediaState) => void;
  sendTargetspotTrackingEvent: (eventType: TargetspotEventType) => void;
  sendTargetspotStateUpdateEvent: (state: HTMLMediaState) => void;
}

export const StationPlayer: React.FC<PlayerStreamProps> = ({
  category,
  playerOpen,
  setPlayerOpen,
  streams,
  adStreams,
  streamsLoadError,
  refetchStreams,
  streamsLoading,
  sendTargetspotTimeUpdateEvent,
  sendTargetspotTrackingEvent,
  sendTargetspotStateUpdateEvent,
}) => {
  const navigatePath = useNavigatePath();
  const t = useTranslate();
  const queryClient = useQueryClient();
  const [liveData, setLiveData] = useState<
    SwaggerResponse<RadioLiveMetadata> | undefined
  >();
  const { data: updatedLiveData, refetch } = useGetPillowRadioLive(
    category.permalink!,
    {
      isPlaying: false,
    }
  );
  const dispatch = useAppDispatch();
  const { session } = useAppSelector(selectUserState);
  const {
    playerEnabled,
    isPlaying,
    fullscreenPlayerInitialyOpen,
    setFullscreenPlayerInitialyOpen,
    setIsMediaLoading,
    setPlayerEnabled,
    setIsPlaying,
    httpMode,
    refetchMetadataInterval,
  } = usePlayerContext();
  const { isHTTPModeEnabled } = useFeaturesContext();
  const { radioQueue, activeMedia } = useAppSelector(selectPlayerState);
  const showStreamErrors = useErrorToast({
    content: t("playback-error"),
  });
  const showHttpStreamErrors = useErrorToast({
    content: t("http-error"),
  });

  const playerLogo = useMemo((): string => {
    const defaultLogo = category.logo || "/radioline.jpg";
    return getStationLiveLogo(liveData?.data, defaultLogo);
  }, [category.logo, liveData?.data]);

  const miniLogo = useMemo((): string | undefined => {
    return getStationLiveLogo(liveData?.data, category.logo!);
  }, [category.logo, liveData?.data]);

  const stationTitle = useMemo(() => {
    return liveData?.data
      ? getStationTitle(liveData.data, category.name!)
      : category.name!;
  }, [
    liveData?.data.body?.content?.show?.name,
    liveData?.data.body?.content?.track?.name,
    category.name,
  ]);

  const artistName = useMemo(() => {
    return liveData?.data ? getArtistName(liveData.data) : undefined;
  }, [liveData?.data.body?.content?.track?.artist?.name]);

  const subtitle = useMemo(() => {
    if (liveData?.data?.body?.content?.show?.name) {
      return liveData?.data?.body?.content?.show?.name;
    }
    if (
      liveData?.data?.body?.content?.track?.artist?.name &&
      liveData?.data?.body?.content?.track?.name
    ) {
      return `${liveData?.data?.body?.content?.track?.artist?.name} - ${liveData?.data?.body?.content?.track?.name}`;
    }
    return "";
  }, [liveData?.data?.body?.content]);

  const artwork = useMemo(
    () => [
      {
        src: playerLogo,
        sizes: "96x96",
        type: "image/png",
      },
      {
        src: playerLogo,
        sizes: "128x128",
        type: "image/png",
      },
      {
        src: playerLogo,
        sizes: "192x192",
        type: "image/png",
      },
      {
        src: playerLogo,
        sizes: "256x256",
        type: "image/png",
      },
      {
        src: playerLogo,
        sizes: "384x384",
        type: "image/png",
      },
      {
        src: playerLogo,
        sizes: "512x512",
        type: "image/png",
      },
    ],
    [playerLogo]
  );
  const { isFavoriteCategory, addToFavorites, removeFromFavorites } =
    useFavorites();

  const httpModeToast = useToast({ content: "Updating the player..." });
  const goToHTTPPlayer = () => {
    httpModeToast();
    console.log(
      "goToHTTPPlayer",
      `http://${window.location.host}/player/${category.permalink}`
    );

    // switch to http mode and open new window
    if (typeof window !== "undefined") {
      window.location.href = `http://${window.location.host}/player/${category.permalink}`; // PROD
      // window.location.href = `http://localhost:3000/player/${category.permalink}`; // DEV
    }
  };

  const backToHTTPS = (mode: "close" | "next" | "prev" = "close") => {
    console.log("backToHTTPS");

    httpModeToast();


    let url = `https://${window.location.host}/`; // PROD
    // let url = `https://localhost:3001/`; // DEV

    if (mode === "close") {
      url += `${category.permalink}#pause`;
    } else if (mode === "next") {
      url += `${category.permalink}#next`;
    } else if (mode === "prev") {
      url += `${category.permalink}#prev`;
    }

    //redirect
    window.location.href = url;
  };

  const router = useRouter();

  useEffect(() => {
    const hash = router.asPath.split("#")[1];

    if (hash && hash === "pause") {
      setPlayerEnabled(false);
      setPlayerOpen(false);
      setIsPlaying(false);
      controls.pause();

      if (radioQueue.items.length > 0) {
        // play other radio
        onNextTrack();
      } else {
        // empty the player

        dispatch(playerActions.selectTrack(null));
      }
    } else if (hash && hash === "next") {
      onNextTrack();
      setPlayerEnabled(true);
      setPlayerOpen(true);
    } else if (hash && hash === "prev") {
      onPreviousTrack();
      setPlayerEnabled(true);
      setPlayerOpen(true);
    }

    if (typeof window !== "undefined") {
      window.location.hash = "";
    }
  }, [router.asPath]);

  const onPlaybackError = (isHttps = true) => {
    console.log("onPlaybackError", isHttps, isHTTPModeEnabled, httpMode);

    if (!isHttps) {
      if (isHTTPModeEnabled && !httpMode) {
        goToHTTPPlayer();
      } else {
        showHttpStreamErrors();
      }
    } else {
      showStreamErrors();
    }
    if (category.permalink) {
      dispatch(playerActions.setFailedPlaybackPermalink(category.permalink));
    }
  };

  useEffect(() => {
    if (streamsLoadError) {
      showStreamErrors();
    }
  }, [streamsLoadError]);

  const isCurrentCategoryFavorite = isFavoriteCategory(category);
  const onAdStreamEnd = () => {
    sendTargetspotTrackingEvent(TargetspotEventType.Complete);
    dispatch(userActions.setLastAdTimestamp(new Date()));
  };

  const onPreviousTrack = () => {
    if (radioQueue.items.length > 0 && radioQueue.activeIndex !== null) {
      if (radioQueue.activeIndex > 0) {
        dispatch(
          playerActions.setRadioQueue({
            items: radioQueue.items,
            activeIndex: radioQueue.activeIndex - 1,
          })
        );
      } else {
        dispatch(
          playerActions.setRadioQueue({
            items: radioQueue.items,
            activeIndex: radioQueue.items.length - 1,
          })
        );
      }
    }

    if (httpMode) {
      backToHTTPS("prev");
    }
  };

  const onNextTrack = () => {
    if (radioQueue.items.length > 0 && radioQueue.activeIndex !== null) {
      if (radioQueue.activeIndex < radioQueue.items.length - 1) {
        dispatch(
          playerActions.setRadioQueue({
            items: radioQueue.items,
            activeIndex: radioQueue.activeIndex + 1,
          })
        );
      } else {
        dispatch(
          playerActions.setRadioQueue({
            items: radioQueue.items,
            activeIndex: 0,
          })
        );
      }
    }

    if (httpMode) {
      backToHTTPS("prev");
    }
  };
  const onFavoriteToggle = (enabled: boolean) => {
    if (enabled) {
      setCategoryToAddToFavorites(category);
    } else {
      setCategoryToRemoveFromFavorites(category);
    }
  };
  const onPlay = () => {
    setPlayerEnabled(true);
    // startAudioDotAdPlayback();
  };

  const { audioElement, fallbackAudioElement, state, controls } = useAppAudio(
    {
      artist: artistName,
      artwork: artwork,
      title: stationTitle,
      isFavorite: isCurrentCategoryFavorite,
      favoritesEnabled: !!session?.user,
      streamType: "radio",
      audioProps: {
        onPlay,
      },
      streams,
      adStreams,
      streamsLoading,
      autoplay: playerEnabled,
      onPlaybackError,
      onAdStreamEnd,
      onNextTrack,
      onFavoriteToggle,
      onPreviousTrack,
      liveStream: true,
      permalink: category.permalink,
    },
    [category.permalink]
  );

  useEffect(() => {
    if (httpMode && state.activeStream?.url?.startsWith("https")) {
      // we are in http mode but the stream is https - switch back to https
      backToHTTPS();
    }
  }, [state.activeStream?.url, httpMode]);

  useInterval(() => {
    if (state.activeAdStream && state.playing) {
      sendTargetspotTimeUpdateEvent(state);
    }
  }, 5000);

  const [categoryToAddToFavorites, setCategoryToAddToFavorites] =
    useState<Category | null>(null);

  useEffect(() => {
    if (categoryToAddToFavorites) {
      addToFavorites(categoryToAddToFavorites, isPlaying);
      setCategoryToAddToFavorites(null);
    }
  }, [categoryToAddToFavorites]);

  const [categoryToRemoveFromFavorites, setCategoryToRemoveFromFavorites] =
    useState<Category | null>(null);

  useEffect(() => {
    if (categoryToRemoveFromFavorites) {
      removeFromFavorites(categoryToRemoveFromFavorites, isPlaying);
      setCategoryToRemoveFromFavorites(null);
    }
  }, [categoryToRemoveFromFavorites]);

  const pausedRefetchCounter = useRef(0);
  const pausedRefetchDebounceAmount = 2;
  useInterval(async () => {
    if (category.permalink) {
      if (state.playing) {
        try {
          const liveLiveData = await getPillowRadioLive(category.permalink!, {
            isPlaying: state.playing,
          });
          setLiveData(liveLiveData);
        } catch (e) {}
      } else {
        // debounce the call when paused
        pausedRefetchCounter.current++;
        if (pausedRefetchCounter.current > pausedRefetchDebounceAmount) {
          refetch();
          pausedRefetchCounter.current = 0;
        }
      }
    }
  }, refetchMetadataInterval);

  // update UI on data change
  useEffect(() => {
    setLiveData(updatedLiveData);
  }, [updatedLiveData]);

  useEffect(() => {
    if (
      radioQueue.activeIndex !== null &&
      radioQueue.items[radioQueue.activeIndex] &&
      radioQueue.items[radioQueue.activeIndex].permalink !== category.permalink
    ) {
      dispatch(
        playerActions.selectTrack({
          podcast: null,
          category: radioQueue.items[radioQueue.activeIndex],
        })
      );
    }
  }, [radioQueue, category]);

  const showOfflineError = useErrorToast({
    content: t("network.connection-lost"),
  });

  useEffect(() => {
    if (!window.navigator.onLine) {
      showOfflineError();
    }
  }, [category.permalink]);

  useEffect(() => {
    if (playerEnabled && fullscreenPlayerInitialyOpen) {
      setPlayerOpen(true);
      setFullscreenPlayerInitialyOpen(false);
    }
  }, [category.permalink, fullscreenPlayerInitialyOpen]);

  const playerVariables = {
    "--image-background": `url(${playerLogo ?? "/gradient.jpg"})`,
  } as React.CSSProperties;

  const goToDetails = () => {
    navigatePath(`/${category.permalink}`);
    setPlayerOpen(false);
  };

  useSpacebarEvent(() => controls.toggle());
  useEffect(() => {
    setIsMediaLoading(state.loading);
  }, [state.loading]);

  useEffect(() => {
    if (isPlaying && !state.playing) {
      controls.play();
    }
    if (!isPlaying && state.playing) {
      controls.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    setIsPlaying(state.playing);
    getPillowRadioLive(category.permalink!, {
      isPlaying: state.playing,
    }).catch((e) => console.log("Could not load live data"));
    if (state.playing) {
      dispatch(playerActions.setFailedPlaybackPermalink(null));
    } else {
      pausedRefetchCounter.current = 0;
    }
    if (state.activeAdStream) {
      sendTargetspotStateUpdateEvent(state);
    }
  }, [state.playing]);

  useEffect(() => {
    if (radioQueue.activeIndex !== null) {
      if (radioQueue.activeIndex === radioQueue.items.length - 1) {
        try {
          const queryNextInfo = useGetPillowRadioLive.info(
            radioQueue.items[0].permalink!,
            { isPlaying: false }
          );
          const queryPrevInfo = useGetPillowRadioLive.info(
            radioQueue.items[radioQueue.activeIndex - 1].permalink!,
            { isPlaying: false }
          );
          queryClient.prefetchQuery(queryPrevInfo.key, () =>
            queryPrevInfo.fun()
          );
          queryClient.prefetchQuery(queryNextInfo.key, () =>
            queryNextInfo.fun()
          );
        } catch (e) {}
      } else if (radioQueue.activeIndex === 0) {
        try {
          const queryNextInfo = useGetPillowRadioLive.info(
            radioQueue.items[1].permalink!,
            { isPlaying: false }
          );
          const queryPrevInfo = useGetPillowRadioLive.info(
            radioQueue.items[radioQueue.items.length - 1].permalink!,
            { isPlaying: false }
          );
          queryClient.prefetchQuery(queryPrevInfo.key, () =>
            queryPrevInfo.fun()
          );
          queryClient.prefetchQuery(queryNextInfo.key, () =>
            queryNextInfo.fun()
          );
        } catch (e) {}
      } else {
        try {
          const queryNextInfo = useGetPillowRadioLive.info(
            radioQueue.items[radioQueue.activeIndex + 1].permalink!,
            { isPlaying: false }
          );
          const queryPrevInfo = useGetPillowRadioLive.info(
            radioQueue.items[radioQueue.activeIndex - 1].permalink!,
            { isPlaying: false }
          );
          queryClient.prefetchQuery(queryPrevInfo.key, () =>
            queryPrevInfo.fun()
          );
          queryClient.prefetchQuery(queryNextInfo.key, () =>
            queryNextInfo.fun()
          );
        } catch (e) {}
      }
    }
  }, [radioQueue]);
  const closePlayerCallback = useCallback(
    () => setPlayerOpen(false),
    [setPlayerOpen]
  );
  return (
    <>
      <div
        className={styles.content_wrapper}
        style={playerVariables}
        onClick={() => setPlayerOpen(true)}
        tabIndex={0}
        data-navigate-item
      >
        {audioElement}
        {fallbackAudioElement}
        <div className={styles.content}>
          <div className={styles.track_info}>
            {miniLogo ? (
              <img className={styles.cover_img} src={miniLogo} alt="" />
            ) : (
              <div className={clsx(styles.cover_img, styles.placeholder)}>
                <MemoWave />
              </div>
            )}
            <div>
              <p className={styles.track_title}>{category.name}</p>
              {subtitle && <p className={styles.track_subtitle}>{subtitle}</p>}
            </div>
          </div>
          <div className={styles.track_controls}>
            <button
              className={styles.play_pause_icon}
              onClick={(e) => {
                if (streamsLoadError) {
                  refetchStreams();
                } else {
                  controls.toggle();
                }
                e.stopPropagation();
              }}
            >
              {!streamsLoadError && (state.loading || streamsLoading) ? (
                <MemoLoader />
              ) : state.playing ? (
                <MemoPause />
              ) : (
                <MemoPlay />
              )}
            </button>
          </div>
        </div>
      </div>
      <PlayerFullScreen
        open={playerOpen}
        category={category}
        cover={playerLogo}
        playbackControls={controls}
        playbackState={state}
        categoryMetadata={liveData?.data}
        playerCssVariables={playerVariables}
        onClose={closePlayerCallback}
        goToDetails={goToDetails}
        backToHTTPS={backToHTTPS}
      />
    </>
  );
};
